import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Tv, ListVideo, Activity, Plus, Sparkles, Clock, AlertTriangle, Trophy, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { apiClient } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { useRealtimeTvs } from '@/hooks/useRealtimeTvs';
import { timeAgo, cn } from '@/utils/cn';

export function DashboardPage() {
  const { tenantId } = useAuthStore();
  useRealtimeTvs(tenantId);

  const { data: tenant } = useQuery({
    queryKey: ['tenant'],
    queryFn: () => apiClient.getTenant().then(r => r.data),
  });
  const { data: tvs = [] } = useQuery({
    queryKey: ['tvs'],
    queryFn: () => apiClient.getTvs().then(r => r.data),
  });
  const { data: playlists = [] } = useQuery({
    queryKey: ['playlists'],
    queryFn: () => apiClient.getPlaylists().then(r => r.data),
  });
  const { data: agentStatus } = useQuery({
    queryKey: ['agent-status'],
    queryFn: () => apiClient.getAgentStatus().then(r => r.data),
  });
  // Stats agregados (horas exibidas, alertas, streak). Atualiza a cada 60s
  // — heartbeat das TVs é a cada 30s, então 60s evita ping-pong sem ficar
  // defasado pra ponto do operador notar.
  const { data: stats } = useQuery({
    queryKey: ['tvs-stats'],
    queryFn: () => apiClient.getTvsStats().then(r => r.data),
    refetchInterval: 60_000,
  });

  const onlineTvs = tvs.filter((t: any) => t.status === 'online').length;
  const offlineTvs = tvs.filter((t: any) => t.status === 'offline').length;

  return (
    <div className="container-app py-8 space-y-8">
      {/* Hero */}
      <div className="rounded-2xl gradient-brand text-white p-8">
        <div className="flex items-center gap-2 text-white/80 text-sm">
          <Sparkles className="h-4 w-4" /> {tenant?.nome_fantasia || tenant?.razao_social}
        </div>
        <h1 className="text-3xl font-bold mt-1">Olá! Bem-vindo de volta.</h1>
        <p className="mt-2 text-white/90">Aqui está um resumo do que está rolando nas suas TVs.</p>

        {/* Stat de vaidade: horas exibidas. Só aparece se houver alguma. */}
        {stats?.total_display_seconds > 0 && (
          <div className="mt-5 flex flex-wrap items-baseline gap-x-6 gap-y-1">
            <div className="flex items-baseline gap-2">
              <Clock className="h-5 w-5 self-center" />
              <span className="text-3xl font-bold">{formatHours(stats.total_display_seconds)}</span>
              <span className="text-white/80 text-sm">de conteúdo exibido</span>
            </div>
            {stats.month_display_seconds > 0 && (
              <div className="text-sm text-white/85">
                <span className="font-semibold">{formatHours(stats.month_display_seconds)}</span> este mês
                {stats.month_delta_pct != null && (
                  <span className={cn(
                    'ml-2 inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-bold',
                    stats.month_delta_pct >= 0 ? 'bg-emerald-500/30' : 'bg-amber-500/30'
                  )}>
                    {stats.month_delta_pct >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {Math.abs(stats.month_delta_pct).toFixed(0)}%
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex gap-3">
          <Button asChild variant="secondary"><Link to="/tvs/add"><Plus className="h-4 w-4" />Adicionar TV</Link></Button>
          <Button asChild variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
            <Link to="/playlists/new">Nova playlist</Link>
          </Button>
        </div>
      </div>

      {/* Alertas — só aparecem quando há sinal real (offline em horário típico ou loop de reload) */}
      {stats?.alerts?.length > 0 && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 space-y-2">
          <div className="flex items-center gap-2 font-semibold text-amber-900">
            <AlertTriangle className="h-4 w-4" />
            {stats.alerts.length === 1 ? '1 alerta' : `${stats.alerts.length} alertas`}
          </div>
          <ul className="space-y-1.5">
            {stats.alerts.map((a: any, i: number) => (
              <li key={i} className="text-sm text-amber-900">
                <Link to={`/tvs/${a.tv_id}`} className="hover:underline">
                  {a.message}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Streak — orgulho discreto */}
      {stats?.streak && (
        <div className="rounded-xl border bg-gradient-to-r from-emerald-50 to-transparent p-4 flex items-center gap-3">
          <Trophy className="h-5 w-5 text-emerald-600" />
          <div className="text-sm">
            <span className="font-bold">{stats.streak.tv_name}</span>
            <span className="text-muted-foreground"> rodando há </span>
            <span className="font-bold text-emerald-700">{stats.streak.days} {stats.streak.days === 1 ? 'dia' : 'dias'}</span>
            <span className="text-muted-foreground"> sem interrupção</span>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard icon={Tv} label="TVs Online" value={onlineTvs} sub={`${tvs.length} no total`} variant="success" />
        <StatCard icon={Tv} label="TVs Offline" value={offlineTvs} sub="Verifique conexão" variant={offlineTvs > 0 ? 'warning' : 'default'} />
        <StatCard icon={ListVideo} label="Playlists ativas" value={playlists.filter((p: any) => p.is_active).length} />
        <StatCard
          icon={Activity}
          label="Agent"
          value={agentStatus?.is_online ? 'Online' : 'Offline'}
          sub={agentStatus?.last_heartbeat_at ? timeAgo(agentStatus.last_heartbeat_at) : 'Nunca'}
          variant={agentStatus?.is_online ? 'success' : 'muted'}
        />
      </div>

      {/* TVs grid */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl">Suas TVs</CardTitle>
          <Button asChild variant="outline" size="sm"><Link to="/tvs">Ver todas</Link></Button>
        </CardHeader>
        <CardContent>
          {tvs.length === 0 ? (
            <div className="text-center py-12">
              <Tv className="h-12 w-12 mx-auto text-muted-foreground" />
              <h3 className="mt-4 font-semibold">Nenhuma TV cadastrada ainda</h3>
              <p className="text-sm text-muted-foreground mt-1">Conecte sua primeira TV em segundos com pareamento plug-and-play.</p>
              <Button asChild className="mt-4"><Link to="/tvs/add">Adicionar TV</Link></Button>
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {tvs.slice(0, 6).map((tv: any) => (
                <Link key={tv.id} to={`/tvs/${tv.id}`} className="rounded-lg border bg-background p-4 hover:shadow-md hover:border-brand-300 transition-all">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold truncate">{tv.name}</div>
                    <StatusDot status={tv.status} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{tv.location || 'Sem localização'}</div>
                  <div className="text-xs text-muted-foreground mt-2">
                    Última atividade: {timeAgo(tv.last_seen_at)}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Formata segundos em "X h" ou "X.Yk h" — números grandes ficam legíveis sem
// virar string gigante. Acima de 9999h vira "10k h" pra caber no hero.
function formatHours(seconds: number): string {
  const hours = seconds / 3600;
  if (hours < 1) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}min`;
  }
  if (hours < 10000) return `${Math.floor(hours).toLocaleString('pt-BR')}h`;
  return `${(hours / 1000).toFixed(1)}k h`;
}

function StatCard({ icon: Icon, label, value, sub, variant = 'default' }: any) {
  const variants: Record<string, string> = {
    default: 'text-foreground',
    success: 'text-emerald-600',
    warning: 'text-amber-600',
    muted: 'text-muted-foreground',
  };
  return (
    <div className="stat-card">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="h-4 w-4" />{label}
      </div>
      <div className={cn('text-3xl font-bold mt-2', variants[variant])}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  const map: Record<string, { color: string; label: string }> = {
    online: { color: 'bg-emerald-500 animate-pulse-slow', label: 'Online' },
    offline: { color: 'bg-red-400', label: 'Offline' },
    pending: { color: 'bg-amber-400', label: 'Pareando' },
    error: { color: 'bg-red-600', label: 'Erro' },
  };
  const v = map[status] ?? { color: 'bg-gray-400', label: status };
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <div className={`h-2 w-2 rounded-full ${v.color}`} />
      <span className="text-muted-foreground">{v.label}</span>
    </div>
  );
}
